import {
  Alert, Button, Col, Form, Row, Spin,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormItem } from '../../../components';
import FormSection from '../../../components/Form/FormSection';
import { SpinnerIcon } from '../../../components/Structure/Loader/Loader';
import RoutePath from '../../../RoutePath';
import {
  fetchTelematicAccountFormConfig,
  fetchTelematicConnectionCreationAttempt,
  fetchLastTelematicCreationAttempt,
} from '../../../store/actions';
import {
  getSelectedCompany,
  getTelematicAccountFormConfig,
  getTelematicConnectionCreationAttempt,
  getTelematicLastCreationAttempt,
} from '../../../store/selectors';
import { uuidv4 } from '../../../utils/uuid';
import SupportButton, { onClickSupportButton } from './SupportButton';
import { Styles } from './TelematicAccount.styled';
import TelematicAccountDetailsSection from './TelematicAccountDetailsSection';
import TelematicProviderChooserSection from './TelematicProviderChooserSection';
import { TelematicAccountOperationType } from '../../../types/enums/telematicAccountFormViewType.enum';

export default function TelematicAccountForm({
  form,
  submitDispatchFunction,
  telematicAccountId,
  initialValues,
  operationType,
  submitClicked,
  generatedCredentials,
}) {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const { t } = useTranslation();

  const { telematicAccountFormConfig, telematicAccountFormConfigLoading } = useSelector(getTelematicAccountFormConfig);
  const creationAttempt = useSelector(getTelematicConnectionCreationAttempt);
  const lastCreationAttempt = useSelector(getTelematicLastCreationAttempt);
  const contextCompanyId = useSelector(getSelectedCompany);

  useEffect(() => {
    dispatch(fetchTelematicAccountFormConfig());
  }, [dispatch]);

  const isCreateOperation = operationType === TelematicAccountOperationType.CREATE;
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isDetailsDisabled, setDetailsDisabled] = useState(null);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(!isCreateOperation);
  const [validExistingProviderForm, setValidExistingProviderForm] = useState(false);
  const [validNewProviderForm, setValidNewProviderForm] = useState(false);
  const [editedFields, setEditedFields] = useState(undefined);
  const [previousAttemptDismissed, setPreviousAttemptDismissed] = useState(false);

  const isActiveButton = (form.getFieldValue('notFromListProviderCheckbox') && isCreateOperation)
    ? validNewProviderForm
    : validExistingProviderForm;

  useEffect(() => {
    if (!selectedProvider || !isCreateOperation) {
      return;
    }
    // Once the account has been created (credentials returned), keep the
    // submit button disabled so the user can't accidentally create another.
    if (generatedCredentials) {
      return;
    }
    // PUSH-mode providers generate credentials server-side, so the user
    // may have nothing to fill in (all fields are autocomplete-driven or
    // there are no fields at all). In that case the form is ready to
    // submit as soon as the provider is selected.
    const userFillableFieldsCount = (selectedProvider.fields ?? [])
      .filter((f) => !f.autocomplete).length;
    const isPushWithNoUserInput = selectedProvider.dataIngestionMode === 'PUSH'
      && userFillableFieldsCount === 0;
    const shouldEnableSubmit = selectedProvider.fields?.length > 0 || isPushWithNoUserInput;
    if (submitButtonDisabled === shouldEnableSubmit) {
      setSubmitButtonDisabled(!shouldEnableSubmit);
    }
    if (isPushWithNoUserInput && !validExistingProviderForm) {
      setValidExistingProviderForm(true);
    }
  }, [
    selectedProvider,
    submitButtonDisabled,
    isCreateOperation,
    validExistingProviderForm,
    generatedCredentials,
  ]);

  useEffect(() => {
    if (selectedProvider && isCreateOperation) {
      setPreviousAttemptDismissed(false);
      dispatch(fetchTelematicConnectionCreationAttempt(contextCompanyId, selectedProvider.adapterType));
    }
  }, [selectedProvider, isCreateOperation, contextCompanyId, dispatch]);

  useEffect(() => {
    if (isCreateOperation) {
      dispatch(fetchLastTelematicCreationAttempt(contextCompanyId));
    }
  }, [isCreateOperation, contextCompanyId, dispatch]);

  // Once the backend returns generated credentials the account already exists.
  // Deactivate the submit button so the user can't accidentally create a
  // duplicate by clicking submit again after closing the success modal.
  useEffect(() => {
    if (generatedCredentials) {
      setSubmitButtonDisabled(true);
      setValidExistingProviderForm(false);
    }
  }, [generatedCredentials]);

  const resetForm = () => {
    setSubmitButtonDisabled(true);
    setValidExistingProviderForm(false);
  };

  function transformTelematicAccountOrUnknownProviderFormToPayload({
    accountDisplayName,
    accountDescription,
    notFromListProviderCheckbox,
    notFromListProviderName,
    notFromListProviderContactEmail,
    notFromListProviderLink,
    ...otherParams
  }) {
    const payload = notFromListProviderCheckbox ? {
      providerName: notFromListProviderName,
      providerContactEmail: notFromListProviderContactEmail,
      providerWebsiteLink: notFromListProviderLink,
    } : {
      accountDescription,
      providerId: selectedProvider.providerId,
      formAttributes: otherParams,
    };

    return {
      payload,
      isUnknownProvider: notFromListProviderCheckbox,
      editedFields,
      resetForm,
      handleUserNeedsSupport: () => onClickSupportButton(telematicAccountId, form, store, history),
    };
  }

  const handleSubmit = () => {
    form.validateFields()
      .then(transformTelematicAccountOrUnknownProviderFormToPayload)
      .then(submitDispatchFunction);
  };

  const resetAccountDetailsFields = (provider) => {
    provider.fields.forEach((field) => {
      form.resetFields([field.fieldName]);
    });
    setValidExistingProviderForm(false);
  };

  const validateNewProvider = () => {
    const newProviderFields = ['notFromListProviderName', 'notFromListProviderContactEmail', 'notFromListProviderLink'];
    if (form.isFieldsTouched(newProviderFields, true)) {
      form.validateFields()
        .then(() => {
          setValidNewProviderForm(true);
        }).catch(() => {
          setValidNewProviderForm(false);
        });
    } else {
      setValidNewProviderForm(false);
    }
  };

  const selectedProviderRequiredFieldsTouched = () => form.isFieldsTouched(['selectedProvider'], true)
    && selectedProvider?.fields.every((field) => form.getFieldValue(field.fieldName) || field.optional);

  const selectedProviderAnyFieldEdited = () => {
    const descriptionEdited = form.getFieldValue('accountDescription') !== (initialValues.accountDescription || '');
    const credentialsEdited = selectedProvider?.fields
      .filter(({ notUpdatable }) => !notUpdatable)
      .some(({ fieldName }) => form.getFieldValue(fieldName) !== initialValues[fieldName]);
    const anyFieldEdited = descriptionEdited || credentialsEdited;
    if (submitButtonDisabled === anyFieldEdited) {
      setSubmitButtonDisabled(!anyFieldEdited);
      setEditedFields({ description: descriptionEdited, credentials: credentialsEdited });
    }
    return anyFieldEdited;
  };

  const validateExistingProvider = () => {
    const canValidateFields = isCreateOperation
      ? selectedProviderRequiredFieldsTouched()
      : selectedProviderAnyFieldEdited();
    if (canValidateFields) {
      form.validateFields()
        .then(() => {
          setValidExistingProviderForm(true);
        }).catch(() => {
          setValidExistingProviderForm(false);
        });
    } else {
      setValidExistingProviderForm(false);
    }
  };

  const preselectProvider = (providerId) => {
    form.setFieldsValue({ selectedProvider: providerId });
  };

  const fillFromPreviousAttempt = () => {
    if (creationAttempt.data?.formData) {
      form.setFieldsValue(creationAttempt.data.formData);
      validateExistingProvider();
      setPreviousAttemptDismissed(true);
    }
  };

  const validateForm = (event) => {
    if (event?.target?.id === 'notFromListProviderCheckbox') {
      return;
    }
    if (form.getFieldValue('notFromListProviderCheckbox') && isCreateOperation) {
      validateNewProvider();
    } else {
      validateExistingProvider(selectedProvider);
    }
  };

  const renderSingleInstructionLink = ({ lang, url }) => {
    const staticLabel = t('TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.INSTRUCTION_LINK_LABEL');
    const linkText = `${lang} ${staticLabel}`;
    return (
      <li key={uuidv4()}>
        <a href={url} target="_blank" rel="noopener noreferrer">{linkText}</a>
      </li>
    );
  };
  const renderIntructionLinks = (links) => links
    .sort((a, b) => a.lang.localeCompare(b.lang))
    .map(renderSingleInstructionLink);

  const renderDetailsBlueBoxDescription = () => {
    const links = selectedProvider.assistance.instructionLinks;
    const hasLinks = links.length > 0;
    const descriptionKey = hasLinks
      ? 'DESCRIPTION_FOR_LINKS'
      : 'DESCRIPTION_NO_LINKS';
    return (
      <>
        {hasLinks && <p>{t('TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.INSTRUCTION_LINKS_HEADER')}</p>}
        <ul>{renderIntructionLinks(links)}</ul>
        <p>{t(`TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.${descriptionKey}`)}</p>
      </>
    );
  };

  const renderForUpdateOnlyDetailsBlueBoxDescription = () => {
    const links = selectedProvider.assistance.instructionLinks;
    const hasLinks = links.length > 0;
    return (
      <>
        {hasLinks && (
          <p>
            {t(
              'TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.INSTRUCTION_LINKS_HEADER',
            )}
          </p>
        )}
        <ul>{renderIntructionLinks(links)}</ul>
        <p>
          {t(
            'TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.DESCRIPTION_FOR_UPDATE_ONLY_LINKS',
          )}
        </p>
      </>
    );
  };

  return (
    <>
      <Spin
        spinning={submitClicked}
        style={{ maxHeight: '100%' }}
        indicator={(
          <SpinnerIcon size="m" />
        )}
      >
        <Form
          initialValues={initialValues}
          onSubmit={handleSubmit}
          form={form}
          style={{ marginTop: '12px', marginBottom: '40px', width: '1100px' }}
          layout="vertical"
          disabled={telematicAccountFormConfigLoading}
          onChange={(event) => validateForm(event)}
        >
          <FormSection title={t(`TELEMATICS.ACCOUNT.${operationType}.SECTIONS.PROVIDER.TITLE`)}>
            <Row justify="left" align="top">
              <TelematicProviderChooserSection
                providers={telematicAccountFormConfig}
                initialProviderId={initialValues.selectedProvider}
                selectedProvider={selectedProvider}
                setSelectedProvider={setSelectedProvider}
                setDetailsDisabled={setDetailsDisabled}
                creationView={isCreateOperation}
                resetAccountDetailsFields={resetAccountDetailsFields}
                lastAttemptAdapterType={lastCreationAttempt.data?.adapterType}
                onPreselectProvider={preselectProvider}
              />
            </Row>
          </FormSection>

          {!isDetailsDisabled && selectedProvider && !selectedProvider.forUpdateOnly ? (
            <FormSection title={t('TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.TITLE')}>
              <Row justify="left" align="top">
                <Col span={11}>
                  {selectedProvider?.fields?.length > 0 ? (
                    <TelematicAccountDetailsSection
                      onSelectChange={(value) => validateForm(value)}
                      selectedProvider={selectedProvider}
                      isDetailsDisabled={isDetailsDisabled}
                      creationView={isCreateOperation}
                      previousAttempt={isCreateOperation && !previousAttemptDismissed ? creationAttempt.data : null}
                      onFillFromPreviousAttempt={fillFromPreviousAttempt}
                      onDismissPreviousAttempt={() => setPreviousAttemptDismissed(true)}
                      generatedCredentials={generatedCredentials}
                    />
                  ) : (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        description={t('TELEMATICS.ACCOUNT.CREATE.HAS_NO_ATTRIBUTES.INFO')}
                        style={Styles.wide}
                      />
                      <div style={{ marginTop: '16px' }}>
                        <FormItem
                          key={uuidv4()}
                          label={t('TELEMATICS.ACCOUNT.CREATE.HAS_NO_ATTRIBUTES.INPUT_LABEL')}
                          name="supportInfo"
                          style={Styles.narrow}
                        >
                          <TextArea style={Styles.wide} />
                        </FormItem>
                      </div>
                    </>
                  )}
                </Col>

                {selectedProvider.assistance.noAssistance
                  ? (
                    <>
                    </>
                  ) : (
                    <Col span={13}>
                      <Row justify="end" align="top">
                        <Alert
                          type="info"
                          showIcon
                          message={(
                            <b>
                              {t(selectedProvider.dataIngestionMode === 'PUSH'
                                ? 'TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.MESSAGE_PUSH'
                                : 'TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.MESSAGE')}
                            </b>
                          )}
                          description={renderDetailsBlueBoxDescription()}
                        />
                      </Row>
                    </Col>
                  )}
              </Row>
            </FormSection>
          )
            : <></>}

          {selectedProvider && selectedProvider.forUpdateOnly ? (
            <Col span={13}>
              <Row justify="left" align="top">
                <Alert
                  type="info"
                  style={Styles.wide}
                  showIcon
                  message={(
                    <b>
                      {t('TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.FOR_UPDATE_ONLY_MESSAGE')}
                    </b>
                  )}
                  description={renderForUpdateOnlyDetailsBlueBoxDescription()}
                />
              </Row>
            </Col>
          ) : <></>}

          <Row align="top" style={{ marginTop: '16px' }}>
            <Col span={11} style={Styles.wide}>
              <Row>
                <Col span={10}>
                  {!isDetailsDisabled && (
                    <Row justify="start">
                      <SupportButton telematicAccountId={telematicAccountId} form={form} />
                    </Row>
                  )}
                </Col>
                <Col span={14}>
                  <Row justify="end">
                    {!isCreateOperation ? (
                      <Button
                        type="link"
                        style={{ marginRight: '8px' }}
                        onClick={() => (history.push(RoutePath.telematicAccountDetails(telematicAccountId)))}
                      >
                        {t('TELEMATICS.ACCOUNT.UPDATE.BUTTON_RETURN_TO_VEHICLE_DETAILS')}
                      </Button>
                    ) : (
                      <Button
                        type="link"
                        style={{ marginRight: '8px' }}
                        onClick={() => (history.push(RoutePath.telematicAccountsView))}
                      >
                        {t('COMMON.CANCEL')}
                      </Button>
                    )}
                    <Button
                      type="primary"
                      disabled={submitButtonDisabled}
                      onClick={handleSubmit}
                      style={isActiveButton
                        ? {
                          background: '#E5FFB0',
                          borderColor: '#E5FFB0',
                          ':hover': { background: '#3f5153', borderColor: '#3f5153', color: '#fff' },
                          ':focus': { background: '#3f5153', borderColor: '#3f5153', color: '#fff' },
                        }
                        : { background: '#BDBDBD', borderColor: '#BDBDBD' }}
                    >
                      {t(operationType === TelematicAccountOperationType.UPDATE ? 'COMMON.SAVE' : 'COMMON.SUBMIT')}
                    </Button>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Spin>
    </>
  );
}

TelematicAccountForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  form: PropTypes.any.isRequired,
  submitDispatchFunction: PropTypes.func.isRequired,
  telematicAccountId: PropTypes.string,
  initialValues: PropTypes.shape({
    accountDisplayName: PropTypes.string,
    accountDescription: PropTypes.string,
    selectedProvider: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    credentials: PropTypes.any,
  }),
  operationType: PropTypes.oneOf(Object.values(TelematicAccountOperationType)).isRequired,
  submitClicked: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  generatedCredentials: PropTypes.object,
};

TelematicAccountForm.defaultProps = {
  telematicAccountId: undefined,
  initialValues: {},
  generatedCredentials: undefined,
};
